import { useEffect, useState } from "react";

import { AddPaymentMethodButton } from "features/payment-methods/components";
import usePaymentSources from "features/payment-methods/datahooks/usePaymentSources";
import type { PaymentSource } from "features/payment-methods/types";

import PoweredByStripe from "common/components/PoweredByStripe";
import { Button } from "common/components/ui/Button";
import { DialogDescription, DialogTitle } from "common/components/ui/Dialog";
import { DrawerDescription, DrawerTitle } from "common/components/ui/Drawer";
import ErrorMessage from "common/components/ui/ErrorMessage";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import PaymentSourceOption from "./PaymentSourceOption";

interface PaymentMethodSelectionProps {
  selectedPaymentSource: PaymentSource;
  addPaymentMethod: () => void;
  onBack: () => void;
  setPaymentSource: (paymentSource: PaymentSource) => void;
  isSettingPaymentSource: boolean;
  setPaymentSourceError: Error;
}

export default function PaymentMethodSelection({
  selectedPaymentSource,
  addPaymentMethod,
  onBack,
  setPaymentSource,
  isSettingPaymentSource,
  setPaymentSourceError,
}: PaymentMethodSelectionProps) {
  const isDesktop = useTwBreakpoint("lg");
  const { paymentSources } = usePaymentSources();

  // Local state for tracking the currently selected payment method
  const [selectedMethod, setSelectedMethod] = useState(selectedPaymentSource);

  useEffect(() => {
    setSelectedMethod(selectedPaymentSource);
  }, [selectedPaymentSource]);

  const Title = isDesktop ? DialogTitle : DrawerTitle;
  const Description = isDesktop ? DialogDescription : DrawerDescription;

  return (
    <>
      <Title>Select your preferred payment method</Title>

      <Description className="mb-8">
        Set a preferred payment method. You can change this at any time
      </Description>

      <div className="flex flex-col gap-3">
        <div className="flex max-h-82 flex-col gap-y-3 overflow-y-auto scrollbar-thin">
          {/* Payment methods */}
          {paymentSources.map((paymentSource) => (
            <PaymentSourceOption
              key={paymentSource.id}
              paymentSource={paymentSource}
              isSelected={selectedMethod?.id === paymentSource.id}
              onSelect={() => setSelectedMethod(paymentSource)}
            />
          ))}
        </div>
        <AddPaymentMethodButton onClick={addPaymentMethod} />
        <PoweredByStripe />
        <ErrorMessage
          error={setPaymentSourceError}
          fallbackErrorMessage="Error setting payment source"
          size="sm"
        />
      </div>

      <div className="flex flex-row items-center justify-between mt-8">
        <Button
          variant="secondary-black"
          size={isDesktop ? "md" : "lg"}
          onClick={onBack}
        >
          Back
        </Button>
        <Button
          size={isDesktop ? "md" : "lg"}
          onClick={() => setPaymentSource(selectedMethod)}
          isLoading={isSettingPaymentSource}
        >
          Confirm
        </Button>
      </div>
    </>
  );
}
